import { useCallback, useState } from "react";
import { useGlobalContext } from "../Context/GlobalContext";

/* =====================================================
   NORMALIZE PACKKY RESTAURANT
===================================================== */

const normalizePackkyRestaurant = (restaurant) => {
  const distance = Number(restaurant?.distance ?? restaurant?.distance_km);

  const rating = Number(restaurant?.rating ?? restaurant?.ratings);

  const deliveryTime = Number(
    restaurant?.delivery_time ?? restaurant?.deliveryTime,
  );

  return {
    shopName: String(
      restaurant?.shop_name ||
        restaurant?.shopName ||
        restaurant?.restaurant_name ||
        restaurant?.organization_name ||
        "Restaurant",
    ).trim(),

    shopCode: String(
      restaurant?.shop_code ||
        restaurant?.shopCode ||
        restaurant?.user_id ||
        restaurant?.userid ||
        "",
    ).trim(),

    distance: Number.isFinite(distance) ? distance : null,

    imageUrl: String(
      restaurant?.image_url ||
        restaurant?.imageUrl ||
        restaurant?.restaurant_profile_image ||
        restaurant?.brand_image ||
        "",
    )
      .trim()
      .replace(/^["']+|["']+$/g, ""),

    rating: Number.isFinite(rating) ? rating : 0,

    deliveryTime:
      Number.isFinite(deliveryTime) && deliveryTime > 0 ? deliveryTime : null,

    websiteLink: String(
      restaurant?.website_link || restaurant?.websiteLink || "",
    ).trim(),

    latitude: restaurant?.latitude ?? restaurant?.lat ?? null,

    longitude:
      restaurant?.longitude ?? restaurant?.lontitude ?? restaurant?.lng ?? null,

    raw: restaurant,
  };
};

/* =====================================================
   NORMALIZE LIKED RESTAURANT
===================================================== */

const normalizeLikedRestaurant = (restaurant) => {
  return {
    id: restaurant?.id,

    customerId: String(restaurant?.customer_id || "").trim(),

    userId: String(restaurant?.fk_user_id || "").trim(),

    organizationName: String(restaurant?.organization_name || "").trim(),

    brandImage: String(restaurant?.brand_image || "").trim(),

    organizationLogo: String(restaurant?.organization_logo || "").trim(),

    imageUrl: String(restaurant?.imageUrl || "").trim(),

    likeStatus: String(restaurant?.like_status || "")
      .trim()
      .toUpperCase(),

    raw: restaurant,
  };
};

/* =====================================================
   NORMALIZE LIKED ITEM
===================================================== */

const normalizeLikedItem = (item) => {
  const customerId = String(item?.customer_id || item?.customerId || "").trim();

  const shopCode = String(
    item?.shop_code ||
      item?.shopCode ||
      item?.user_id ||
      item?.fk_user_id ||
      item?.userid ||
      "",
  ).trim();

  const itemId = String(
    item?.item_id ||
      item?.itemId ||
      item?.pk_item_id ||
      item?.pk_catalog_item_id ||
      item?.id ||
      "",
  ).trim();

  const itemStatus = String(
    item?.item_status ||
      item?.itemStatus ||
      item?.like_status ||
      item?.status ||
      "",
  )
    .trim()
    .toUpperCase();

  return {
    id: item?.id ?? null,

    customerId,

    shopCode,

    itemId,

    itemStatus,

    itemName: String(
      item?.item_name ||
        item?.itemName ||
        item?.product_name ||
        item?.productName ||
        item?.name ||
        "",
    ).trim(),

    itemImage: String(
      item?.item_image ||
        item?.itemImage ||
        item?.image_url ||
        item?.imageUrl ||
        item?.image ||
        "",
    )
      .trim()
      .replace(/^["']+|["']+$/g, ""),

    restaurantName: String(
      item?.organization_name || item?.restaurant_name || item?.shop_name || "",
    ).trim(),

    raw: item,
  };
};

const normalizeFavouriteItemsResponse = (responseData) => {
  const favourites = Array.isArray(responseData?.response?.favourites)
    ? responseData.response.favourites
    : [];

  return favourites.flatMap((restaurant) => {
    const shopCode = String(
      restaurant?.us_code || restaurant?.shop_code || restaurant?.user_id || "",
    ).trim();

    const restaurantName = String(
      restaurant?.restaurant_name ||
        restaurant?.organization_name ||
        restaurant?.shop_name ||
        "",
    ).trim();

    const itemStatus = String(restaurant?.liked_item_status || "Y")
      .trim()
      .toUpperCase();

    const itemList = Array.isArray(restaurant?.item_list)
      ? restaurant.item_list
      : [];

  const uniqueItems = new Map();

  itemList.forEach((item) => {
    const key = String(item.item_id || item.id).trim();

    if (!uniqueItems.has(key)) {
      uniqueItems.set(key, {
        id: item?.id ?? null,
        customerId: "",
        shopCode,
        itemId: key,
        itemStatus,
        itemName: String(item?.item_name || "").trim(),
        itemImage: String(item?.photo || "")
          .trim()
          .replace(/^["']+|["']+$/g, ""),
        restaurantName,
        merchantId: restaurant?.merchant_id ?? null,
        restaurantRating: Number(restaurant?.rating || 0),
        restaurantDistance: Number(restaurant?.distance || 0),
        restaurantLogo: String(restaurant?.logo || "").trim(),
        restaurantAddress: [
          restaurant?.street,
          restaurant?.city,
          restaurant?.state,
        ]
          .filter(Boolean)
          .join(", "),
        variants: Array.isArray(item?.variant_list) ? item.variant_list : [],
        raw: {
          ...item,
          shop_code: shopCode,
          us_code: shopCode,
          restaurant_name: restaurantName,
          organization_name: restaurantName,
        },
      });
    }
  });

  return [...uniqueItems.values()];
  });
};

/* =====================================================
   ITEM KEY
===================================================== */

const getItemKey = (shopCode, itemId) => {
  const cleanShopCode = String(shopCode || "").trim();

  const cleanItemId = String(itemId || "").trim();

  return `${cleanShopCode}::${cleanItemId}`;
};

/* =====================================================
   EXTRACT API ARRAY
===================================================== */

const extractResponseList = (responseData) => {
  if (Array.isArray(responseData?.response)) {
    return responseData.response;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }

  if (Array.isArray(responseData?.likedItems)) {
    return responseData.likedItems;
  }

  if (Array.isArray(responseData?.likeDislikeItems)) {
    return responseData.likeDislikeItems;
  }

  if (Array.isArray(responseData)) {
    return responseData;
  }

  return [];
};

/* =====================================================
   HOOK
===================================================== */

export const useRestaurantList = () => {
  const { api, packkyApi } = useGlobalContext();

  /* =====================================================
     NEARBY RESTAURANT STATE
  ===================================================== */

  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);

  const [nearbyRestaurantLoading, setNearbyRestaurantLoading] = useState(false);

  const [nearbyRestaurantError, setNearbyRestaurantError] = useState("");

  /* =====================================================
     LIKED RESTAURANT STATE
  ===================================================== */

  const [likedRestaurantDetails, setLikedRestaurantDetails] = useState([]);

  const [likedRestaurantUserIds, setLikedRestaurantUserIds] = useState([]);

  const [likedRestaurantLoading, setLikedRestaurantLoading] = useState(false);

  const [likedRestaurantError, setLikedRestaurantError] = useState("");

  /* =====================================================
     LIKED ITEM STATE
  ===================================================== */

  const [likedItemDetails, setLikedItemDetails] = useState([]);

  const [likedItemKeys, setLikedItemKeys] = useState([]);

  const [likedItemLoading, setLikedItemLoading] = useState(false);

  const [likedItemUpdating, setLikedItemUpdating] = useState(false);

  const [likedItemError, setLikedItemError] = useState("");

  /* =====================================================
     GET PACKKY RESTAURANTS
  ===================================================== */

  const getRestaurantList = useCallback(
    async ({ customerLatitude, customerLongitude, range = 10, type = "" }) => {
      const latitude = String(customerLatitude ?? "").trim();

      const longitude = String(customerLongitude ?? "").trim();

      if (!latitude || !longitude) {
        const message = "Customer latitude and longitude are required.";

        setNearbyRestaurantError(message);
        setNearbyRestaurants([]);

        return {
          success: false,
          data: [],
          message,
        };
      }

      if (!packkyApi) {
        const message = "Packky API instance is not available.";

        setNearbyRestaurantError(message);
        setNearbyRestaurants([]);

        return {
          success: false,
          data: [],
          message,
        };
      }

      try {
        setNearbyRestaurantLoading(true);
        setNearbyRestaurantError("");

        const payload = {
          customerLatitude: latitude,
          customerLongitude: longitude,
          range: Number(range) || 20,
          type: String(type || "").trim(),
        };

        const response = await packkyApi.post(
          "/Shop/v1.0/getRestaurantList",
          payload,
        );

        const responseData = response?.data;

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              "Unable to fetch nearby restaurants.",
          );
        }

        const restaurantList = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.response)
            ? responseData.response
            : Array.isArray(responseData?.restaurants)
              ? responseData.restaurants
              : Array.isArray(responseData?.restaurantList)
                ? responseData.restaurantList
                : Array.isArray(responseData)
                  ? responseData
                  : [];

        const normalizedRestaurants = restaurantList.map(
          normalizePackkyRestaurant,
        );

        setNearbyRestaurants(normalizedRestaurants);

        return {
          success: true,
          data: normalizedRestaurants,
          message: responseData?.responseMessage || "",
        };
      } catch (error) {
        console.error(
          "Packky restaurant API error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch nearby restaurants.";

        setNearbyRestaurants([]);
        setNearbyRestaurantError(message);

        return {
          success: false,
          data: [],
          message,
        };
      } finally {
        setNearbyRestaurantLoading(false);
      }
    },
    [packkyApi],
  );

  /* =====================================================
     GET LIKED RESTAURANTS
  ===================================================== */

 const getLikeRestaurantDetails = useCallback(
   async ({ customerId, latitude = "", longitude = "", range = "10" } = {}) => {
     const cleanCustomerId = String(customerId || "").trim();
     const cleanLatitude = String(latitude ?? "").trim();
     const cleanLongitude = String(longitude ?? "").trim();
     const cleanRange = String(range ?? "10").trim();

     if (!cleanCustomerId) {
       const message = "Customer ID is required.";

       setLikedRestaurantDetails([]);
       setLikedRestaurantUserIds([]);
       setLikedRestaurantError(message);

       return {
         success: false,
         data: [],
         likedUserIds: [],
         message,
       };
     }

     if (!api) {
       const message = "Anlook API instance is not available.";

       setLikedRestaurantDetails([]);
       setLikedRestaurantUserIds([]);
       setLikedRestaurantError(message);

       return {
         success: false,
         data: [],
         likedUserIds: [],
         message,
       };
     }

     try {
       setLikedRestaurantLoading(true);
       setLikedRestaurantError("");

       const payload = {
         customerId: cleanCustomerId,
         latitude: cleanLatitude,
         longitude: cleanLongitude,
         range: cleanRange,
       };

       console.log("Get liked restaurants payload:", payload);

       const response = await api.post(
         "/Chaafo/v1.0/getLikeRestaurantDetails",
         payload,
         {
           headers: {
             Accept: "*/*",
             "Content-Type": "application/json-patch+json",
           },
         },
       );

       const responseData = response?.data;

       console.log("Get liked restaurants response:", responseData);

       if (String(responseData?.responseCode) !== "00") {
         throw new Error(
           responseData?.responseMessage ||
             "Unable to fetch liked restaurants.",
         );
       }

       const responseList = Array.isArray(responseData?.response)
         ? responseData.response
         : Array.isArray(responseData?.data)
           ? responseData.data
           : Array.isArray(responseData?.restaurants)
             ? responseData.restaurants
             : Array.isArray(responseData)
               ? responseData
               : [];

       const normalizedList = responseList.map(normalizeLikedRestaurant);

       const likedUserIds = [
         ...new Set(
           normalizedList
             .filter(
               (restaurant) =>
                 restaurant.likeStatus === "Y" && restaurant.userId,
             )
             .map((restaurant) => restaurant.userId),
         ),
       ];

       setLikedRestaurantDetails(normalizedList);
       setLikedRestaurantUserIds(likedUserIds);

       return {
         success: true,
         data: normalizedList,
         likedUserIds,
         message: responseData?.responseMessage || "",
         raw: responseData,
       };
     } catch (error) {
       console.error(
         "Get liked restaurants API error:",
         error?.response?.data || error,
       );

       const message =
         error?.response?.data?.responseMessage ||
         error?.response?.data?.message ||
         error?.message ||
         "Unable to fetch liked restaurants.";

       setLikedRestaurantDetails([]);
       setLikedRestaurantUserIds([]);
       setLikedRestaurantError(message);

       return {
         success: false,
         data: [],
         likedUserIds: [],
         message,
       };
     } finally {
       setLikedRestaurantLoading(false);
     }
   },
   [api],
 );

  /* =====================================================
     LIKE / DISLIKE RESTAURANT
  ===================================================== */

  const likeDislikeRestaurant = useCallback(
    async ({ customerId, userId, status }) => {
      const cleanCustomerId = String(customerId || "").trim();

      const cleanUserId = String(userId || "").trim();

      const cleanStatus = String(status || "")
        .trim()
        .toUpperCase();

      if (!cleanCustomerId) {
        return {
          success: false,
          message: "Customer ID is required.",
        };
      }

      if (!cleanUserId) {
        return {
          success: false,
          message: "Restaurant user ID is required.",
        };
      }

      if (cleanStatus !== "Y" && cleanStatus !== "N") {
        return {
          success: false,
          message: "Like status must be Y or N.",
        };
      }

      if (!api) {
        return {
          success: false,
          message: "Anlook API instance is not available.",
        };
      }

      try {
        const payload = {
          customerId: cleanCustomerId,
          userId: cleanUserId,
          status: cleanStatus,
        };

        const response = await api.post(
          "/Chaafo/v1.0/likeDislikeRestaurant",
          payload,
        );

        const responseData = response?.data;

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              "Unable to update restaurant like.",
          );
        }

        setLikedRestaurantUserIds((previous) => {
          if (cleanStatus === "Y") {
            return previous.includes(cleanUserId)
              ? previous
              : [...previous, cleanUserId];
          }

          return previous.filter((id) => id !== cleanUserId);
        });

        setLikedRestaurantDetails((previous) => {
          if (cleanStatus === "N") {
            return previous.filter(
              (restaurant) => restaurant.userId !== cleanUserId,
            );
          }

          const alreadyExists = previous.some(
            (restaurant) => restaurant.userId === cleanUserId,
          );

          if (alreadyExists) {
            return previous.map((restaurant) =>
              restaurant.userId === cleanUserId
                ? {
                    ...restaurant,
                    likeStatus: "Y",
                  }
                : restaurant,
            );
          }

          return previous;
        });

        return {
          success: true,
          status: cleanStatus,
          message: responseData?.responseMessage || "",
        };
      } catch (error) {
        console.error(
          "Like/dislike restaurant API error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update restaurant like.";

        return {
          success: false,
          message,
        };
      }
    },
    [api],
  );

  /* =====================================================
     GET LIKE / DISLIKE ITEMS
     POST /Chaafo/getLikeDislikeItems
  ===================================================== */

  const getLikeDislikeItems = useCallback(
    async ({ customerId, latitude = "", longitude = "", range = "" } = {}) => {
      const cleanCustomerId = String(customerId || "").trim();

      const cleanLatitude = String(latitude ?? "").trim();

      const cleanLongitude = String(longitude ?? "").trim();

      const cleanRange = String(range ?? "").trim();

      if (!cleanCustomerId) {
        const message = "Customer ID is required.";

        setLikedItemDetails([]);
        setLikedItemKeys([]);
        setLikedItemError(message);

        return {
          success: false,
          data: [],
          likedItemKeys: [],
          message,
        };
      }

      if (!api) {
        const message = "Anlook API instance is not available.";

        setLikedItemDetails([]);
        setLikedItemKeys([]);
        setLikedItemError(message);

        return {
          success: false,
          data: [],
          likedItemKeys: [],
          message,
        };
      }

      try {
        setLikedItemLoading(true);
        setLikedItemError("");

        const payload = {
          customer_id: cleanCustomerId,
          latitude: cleanLatitude,
          longitude: cleanLongitude,
          range: cleanRange,
        };

        console.log("Get liked items payload:", payload);

        const response = await api.post(
          "/Chaafo/getLikeDislikeItems",
          payload,
          {
            headers: {
              Accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        console.log("Get liked items response:", responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to fetch liked items.",
          );
        }

      const normalizedList = normalizeFavouriteItemsResponse(responseData);

        const activeLikedItemKeys = [
          ...new Set(
            normalizedList
              .filter(
                (item) =>
                  item.itemStatus === "Y" && item.shopCode && item.itemId,
              )
              .map((item) => getItemKey(item.shopCode, item.itemId)),
          ),
        ];

        setLikedItemDetails(normalizedList);

        setLikedItemKeys(activeLikedItemKeys);

        return {
          success: true,
          data: normalizedList,
          likedItemKeys: activeLikedItemKeys,
          message: responseData?.responseMessage || "",
          raw: responseData,
        };
      } catch (error) {
        console.error(
          "Get like/dislike items API error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch liked items.";

        setLikedItemDetails([]);
        setLikedItemKeys([]);
        setLikedItemError(message);

        return {
          success: false,
          data: [],
          likedItemKeys: [],
          message,
        };
      } finally {
        setLikedItemLoading(false);
      }
    },
    [api],
  );

  /* =====================================================
     INSERT LIKE / DISLIKE ITEM
     POST /Chaafo/insertLikeDislikeItems
  ===================================================== */

  const insertLikeDislikeItems = useCallback(
    async ({ customerId, shopCode, itemId, itemStatus } = {}) => {
      const cleanCustomerId = String(customerId || "").trim();

      const cleanShopCode = String(shopCode || "").trim();

      const cleanItemId = String(itemId || "").trim();

      const cleanItemStatus = String(itemStatus || "")
        .trim()
        .toUpperCase();

      if (!cleanCustomerId) {
        return {
          success: false,
          message: "Customer ID is required.",
        };
      }

      if (!cleanShopCode) {
        return {
          success: false,
          message: "Shop code is required.",
        };
      }

      if (!cleanItemId) {
        return {
          success: false,
          message: "Item ID is required.",
        };
      }

      if (cleanItemStatus !== "Y" && cleanItemStatus !== "N") {
        return {
          success: false,
          message: "Item status must be Y or N.",
        };
      }

      if (!api) {
        return {
          success: false,
          message: "Anlook API instance is not available.",
        };
      }

      try {
        setLikedItemUpdating(true);
        setLikedItemError("");

        const payload = {
          customer_id: cleanCustomerId,
          shop_code: cleanShopCode,
          item_id: cleanItemId,
          item_status: cleanItemStatus,
        };

        console.log("Insert item like payload:", payload);

        const response = await api.post(
          "/Chaafo/insertLikeDislikeItems",
          payload,
          {
            headers: {
              Accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        console.log("Insert item like response:", responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage ||
              "Unable to update item like status.",
          );
        }

        const itemKey = getItemKey(cleanShopCode, cleanItemId);

        setLikedItemKeys((previous) => {
          if (cleanItemStatus === "Y") {
            return previous.includes(itemKey)
              ? previous
              : [...previous, itemKey];
          }

          return previous.filter((key) => key !== itemKey);
        });

        setLikedItemDetails((previous) => {
          const existingIndex = previous.findIndex(
            (item) =>
              item.shopCode === cleanShopCode && item.itemId === cleanItemId,
          );

          if (cleanItemStatus === "N") {
            return previous.filter(
              (item) =>
                !(
                  item.shopCode === cleanShopCode && item.itemId === cleanItemId
                ),
            );
          }

          if (existingIndex >= 0) {
            return previous.map((item, index) => {
              if (index !== existingIndex) {
                return item;
              }

              return {
                ...item,
                customerId: cleanCustomerId,
                shopCode: cleanShopCode,
                itemId: cleanItemId,
                itemStatus: "Y",
              };
            });
          }

          return [
            ...previous,
            {
              id: null,
              customerId: cleanCustomerId,
              shopCode: cleanShopCode,
              itemId: cleanItemId,
              itemStatus: "Y",
              itemName: "",
              itemImage: "",
              restaurantName: "",
              raw: responseData,
            },
          ];
        });

        return {
          success: true,
          status: cleanItemStatus,
          itemKey,
          message: responseData?.responseMessage || "",
          data: responseData,
        };
      } catch (error) {
        console.error(
          "Insert like/dislike item API error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update item like status.";

        setLikedItemError(message);

        return {
          success: false,
          message,
        };
      } finally {
        setLikedItemUpdating(false);
      }
    },
    [api],
  );

  /* =====================================================
     CHECK ITEM LIKED
  ===================================================== */

  const isItemLiked = useCallback(
    ({ shopCode, itemId }) => {
      const itemKey = getItemKey(shopCode, itemId);

      return likedItemKeys.includes(itemKey);
    },
    [likedItemKeys],
  );

  /* =====================================================
     RESET FUNCTIONS
  ===================================================== */

  const resetNearbyRestaurants = useCallback(() => {
    setNearbyRestaurants([]);
    setNearbyRestaurantError("");
  }, []);

  const resetLikedRestaurants = useCallback(() => {
    setLikedRestaurantDetails([]);
    setLikedRestaurantUserIds([]);
    setLikedRestaurantError("");
  }, []);

  const resetLikedItems = useCallback(() => {
    setLikedItemDetails([]);
    setLikedItemKeys([]);
    setLikedItemError("");
  }, []);

  /* =====================================================
     RETURN
  ===================================================== */

  return {
    // Nearby restaurants
    nearbyRestaurants,
    getRestaurantList,
    resetNearbyRestaurants,
    nearbyRestaurantLoading,
    nearbyRestaurantError,
    setNearbyRestaurantError,

    // Restaurant like/dislike
    likedRestaurantDetails,
    likedRestaurantUserIds,
    likedRestaurantLoading,
    likedRestaurantError,
    getLikeRestaurantDetails,
    likeDislikeRestaurant,
    resetLikedRestaurants,

    // Item like/dislike
    likedItemDetails,
    likedItemKeys,
    likedItemLoading,
    likedItemUpdating,
    likedItemError,
    getLikeDislikeItems,
    insertLikeDislikeItems,
    isItemLiked,
    resetLikedItems,
  };
};
